import React from "react";
import {
    fetchUserPreferences,
    getCurrentUserPreferenceKey,
    saveUserPreference,
} from "../../utils/userPreferences";
import {
    MAIL_ANSICHT_LOCAL_KEY,
    MAIL_ANSICHT_SCOPE,
    normalisiereMailAnsicht,
    type MailAnsicht,
} from "./mailAnsicht";

function leseLokal(): MailAnsicht {
    try {
        return normalisiereMailAnsicht(window.localStorage.getItem(MAIL_ANSICHT_LOCAL_KEY));
    } catch {
        return normalisiereMailAnsicht(null);
    }
}

function schreibeLokal(ansicht: MailAnsicht): void {
    try {
        window.localStorage.setItem(MAIL_ANSICHT_LOCAL_KEY, ansicht);
    } catch {
        /* privates Fenster / blockierter Speicher — der Serverwert trägt */
    }
}

/*
 * Gemeinsamer Stand für ALLE Stellen, die die Wahl zeigen oder ändern: den
 * Schalter in der Funktionsleiste der Mail-App und den Reiter „Darstellung" im
 * Mail-Einstellungsfenster. Vorher hielt jede Hook-Instanz ihren eigenen
 * useState — eine Wahl in den Einstellungen wäre in der offenen Mail-App erst
 * nach dem Neuladen angekommen.
 *
 * Andere Browser-Tabs erreicht die Wahl über das `storage`-Ereignis.
 */
let stand: MailAnsicht | null = null;
const hoerer = new Set<() => void>();
/** Hat die Person in dieser Sitzung schon selbst gewählt? Dann darf ein spät
 *  eintreffender Serverwert die Wahl nicht wieder umwerfen. */
let selbstGewaehlt = false;
let serverAbfrage: Promise<void> | null = null;
let storageHoererAktiv = false;

function aktuellerStand(): MailAnsicht {
    if (stand === null) stand = leseLokal();
    return stand;
}

function setzeStand(next: MailAnsicht): void {
    if (aktuellerStand() === next) return;
    stand = next;
    for (const h of Array.from(hoerer)) h();
}

function beiStorage(e: StorageEvent): void {
    if (e.key !== MAIL_ANSICHT_LOCAL_KEY) return;
    setzeStand(normalisiereMailAnsicht(e.newValue));
}

/** Serverwert einmal je Seitenaufruf holen — nicht je Instanz. */
function ladeVomServer(): void {
    if (serverAbfrage) return;
    serverAbfrage = fetchUserPreferences([MAIL_ANSICHT_SCOPE])
        .then((werte) => {
            if (selbstGewaehlt) return;
            if (!(MAIL_ANSICHT_SCOPE in werte)) return;
            const vomServer = normalisiereMailAnsicht(werte[MAIL_ANSICHT_SCOPE]);
            schreibeLokal(vomServer);
            setzeStand(vomServer);
        })
        .catch(() => {
            /* Server nicht erreichbar — der lokale Startwert bleibt */
        });
}

function abonniere(h: () => void): () => void {
    hoerer.add(h);
    if (!storageHoererAktiv && typeof window !== "undefined") {
        window.addEventListener("storage", beiStorage);
        storageHoererAktiv = true;
    }
    return () => {
        hoerer.delete(h);
        if (hoerer.size === 0 && storageHoererAktiv) {
            window.removeEventListener("storage", beiStorage);
            storageHoererAktiv = false;
        }
    };
}

/** Speichert eine neue Wahl: sofort für alle offenen Stellen, im Browser und
 *  (best effort) beim Server. */
export function waehleMailAnsicht(next: MailAnsicht): void {
    selbstGewaehlt = true;
    if (aktuellerStand() === next) return;
    schreibeLokal(next);
    setzeStand(next);
    void (async () => {
        try {
            const userKey = await getCurrentUserPreferenceKey();
            if (!userKey) return;
            await saveUserPreference(userKey, MAIL_ANSICHT_SCOPE, next);
        } catch {
            /* best effort — die Wahl gilt in dieser Sitzung trotzdem */
        }
    })();
}

/** Nur für Tests: der Modul-Zustand überlebt sonst von Testfall zu Testfall. */
export function __setzeMailAnsichtZurueck(): void {
    stand = null;
    selbstGewaehlt = false;
    serverAbfrage = null;
    hoerer.clear();
    if (storageHoererAktiv && typeof window !== "undefined") {
        window.removeEventListener("storage", beiStorage);
    }
    storageHoererAktiv = false;
}

/**
 * T-29 — die gewählte Mail-Darstellung einer Person.
 *
 * Startwert kommt sofort aus dem Browser (kein Flackern zwischen den
 * Ansichten), danach gewinnt der Serverwert aus der `UserPreference`-Ablage —
 * so folgt die Wahl der Person auch auf einen anderen Rechner. Speichern ist
 * best effort: scheitert es, bleibt die Wahl in dieser Sitzung trotzdem stehen.
 *
 * Alle Instanzen teilen EINEN Stand (siehe oben): eine Wahl im
 * Einstellungsfenster schaltet die offene Mail-App sofort mit um.
 */
export function useMailAnsicht(): {
    ansicht: MailAnsicht;
    setAnsicht: (next: MailAnsicht) => void;
} {
    const ansicht = React.useSyncExternalStore(abonniere, aktuellerStand, aktuellerStand);

    React.useEffect(() => {
        ladeVomServer();
    }, []);

    return { ansicht, setAnsicht: waehleMailAnsicht };
}
